(() => {
  class TLSDetails extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.scope = document.getElementById('MainContent');
      if (!this.scope) return;
      this.media = matchMedia('(min-width: 990px)');
      this.states = new Map();
      this.active = '';
      this.onMode = () => this.refresh();
      this.onHash = () => this.openHash(location.hash, true);
      this.onClick = event => {
        const link = event.target.closest('a[data-tls-pdp-link],a[data-decision-measure]');
        if (!link || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        const url = new URL(link.href, location.href);
        if (url.origin !== location.origin || url.pathname !== location.pathname) return;
        if (this.openHash(url.hash, true)) {
          event.preventDefault();
          event.stopImmediatePropagation();
          history.pushState(null, '', url.hash);
        }
      };
      this.onKeys = event => {
        const tabs = [...this.querySelectorAll('[role="tab"]')];
        const index = tabs.indexOf(event.target);
        if (index < 0 || !['ArrowRight','ArrowLeft','Home','End'].includes(event.key)) return;
        event.preventDefault();
        const next = event.key === 'Home' ? 0 : event.key === 'End' ? tabs.length - 1 : (index + (event.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;
        tabs[next].click();
        tabs[next].focus();
      };
      this.addEventListener('keydown', this.onKeys);
      document.addEventListener('click', this.onClick, true);
      window.addEventListener('hashchange', this.onHash);
      window.addEventListener('popstate', this.onHash);
      this.media.addEventListener('change', this.onMode);
      this.observer = new MutationObserver(() => {
        cancelAnimationFrame(this.frame);
        this.frame = requestAnimationFrame(() => this.refresh());
      });
      this.refresh();
      this.observe();
      this.openHash(location.hash, false);
    }
    observe() { this.observer.observe(this.scope, {childList:true,subtree:true}); }
    refresh() {
      this.observer.disconnect();
      const order = ['overview','specifications','size','customization','shipping','faq'];
      this.entries = [];
      let cursor = this.closest('.shopify-section');
      for (const key of order) {
        const content = this.scope.querySelector(`[data-pdp-section="${key}"]`);
        let outer = this.scope.querySelector(`[data-tls-panel="${key}"]`);
        if (!content) { if (outer) outer.hidden = true; continue; }
        const wrapper = key === 'customization' ? content : content.closest('.shopify-section');
        if (!wrapper) continue;
        if (!outer) {
          outer = document.createElement('div');
          outer.dataset.tlsPanel = key;
          outer.className = 'tls-details-panel';
          wrapper.before(outer);
          const toggle = document.createElement('button');
          toggle.type = 'button';
          toggle.className = 'tls-details-toggle page-width';
          toggle.id = `TLSDetailsToggle-${this.dataset.productId}-${key}`;
          toggle.textContent = this.dataset[key];
          outer.append(toggle, wrapper);
          toggle.addEventListener('click', () => {
            this.states.set(key, !this.states.get(key));
            this.apply();
          });
        }
        outer.hidden = false;
        if (cursor && cursor.nextElementSibling !== outer) cursor.after(outer);
        cursor = outer;
        wrapper.id ||= `TLSDetailsBody-${this.dataset.productId}-${key}`;
        if (!this.states.has(key)) this.states.set(key, key === 'overview' || (!this.entries.length && !this.scope.querySelector('[data-pdp-section="overview"]')));
        this.entries.push({key,outer,wrapper,toggle:outer.querySelector('.tls-details-toggle')});
      }
      if (!this.entries.some(entry => entry.key === this.active)) this.active = this.entries[0]?.key || '';
      this.replaceChildren();
      const nav = document.createElement('div');
      nav.setAttribute('role','tablist');
      nav.setAttribute('aria-label',this.dataset.label);
      for (const entry of this.entries) {
        const tab = document.createElement('button');
        tab.type = 'button';
        tab.id = `TLSDetailsTab-${this.dataset.productId}-${entry.key}`;
        tab.setAttribute('role','tab');
        tab.setAttribute('aria-controls',entry.wrapper.id);
        tab.textContent = this.dataset[entry.key];
        tab.addEventListener('click', () => { this.active = entry.key; this.apply(); });
        nav.append(tab);
        entry.tab = tab;
      }
      this.append(nav);
      this.scope.querySelectorAll('[data-tls-returns]').forEach(link => { link.hidden = false; });
      // The server-rendered boolean controls the fourth service entry.
      this.apply();
      this.observe();
    }
    apply() {
      const desktop = this.media.matches;
      this.scope.querySelectorAll('.tls-description-photos').forEach(photos => {
        if (desktop) {
          if (photos.dataset.desktop !== 'true') photos.dataset.mobileOpen = String(photos.open);
          photos.dataset.desktop = 'true';
          photos.open = true;
        } else if (photos.dataset.desktop === 'true') {
          photos.open = photos.dataset.mobileOpen === 'true';
          photos.dataset.desktop = 'false';
        }
      });
      this.hidden = !desktop || this.entries.length < 2;
      for (const entry of this.entries) {
        const opened = desktop ? entry.key === this.active : this.states.get(entry.key);
        if (!desktop && entry.key === 'faq') entry.wrapper.querySelectorAll('details').forEach(detail => {
          if (!detail.dataset.initializedAnswer && detail.textContent.length < 500) {
            detail.open = true;
            detail.dataset.initializedAnswer = 'true';
          }
        });
        entry.wrapper.hidden = !opened;
        entry.toggle.hidden = desktop;
        entry.toggle.setAttribute('aria-expanded',String(Boolean(opened)));
        entry.toggle.setAttribute('aria-controls',entry.wrapper.id);
        entry.tab.setAttribute('aria-selected',String(entry.key === this.active));
        entry.tab.tabIndex = entry.key === this.active ? 0 : -1;
        if (desktop && this.entries.length > 1) {
          entry.wrapper.setAttribute('role','tabpanel');
          entry.wrapper.setAttribute('aria-labelledby',entry.tab.id);
        } else {
          entry.wrapper.removeAttribute('role');
          entry.wrapper.removeAttribute('aria-labelledby');
        }
        entry.outer.classList.toggle('tls-details-panel--mobile',!desktop);
      }
      this.scope.querySelectorAll('a[data-tls-pdp-link]').forEach(link => {
        const id = new URL(link.href,location.href).hash.slice(1);
        if (id.startsWith('TLSPDP')) link.hidden = !document.getElementById(id);
      });
      this.scope.querySelectorAll('[data-tls-returns]').forEach(link => { link.hidden = false; });
    }
    openHash(hash, focus) {
      if (!hash || hash === '#') return false;
      let target;
      try { target = document.getElementById(decodeURIComponent(hash.slice(1))); } catch { return false; }
      const entry = this.entries.find(item => item.outer.contains(target));
      if (!entry) return false;
      this.active = entry.key;
      this.states.set(entry.key,true);
      this.apply();
      for (let node = target; node && node !== entry.outer; node = node.parentElement) if (node.tagName === 'DETAILS') node.open = true;
      if (focus) {
        target.tabIndex = -1;
        target.focus({preventScroll:true});
        target.scrollIntoView({block:'start'});
      }
      return true;
    }
    disconnectedCallback() {
      this.observer?.disconnect();
      cancelAnimationFrame(this.frame);
      document.removeEventListener('click',this.onClick,true);
      window.removeEventListener('hashchange',this.onHash);
      window.removeEventListener('popstate',this.onHash);
      this.media?.removeEventListener('change',this.onMode);
    }
  }
  if (!customElements.get('tls-pdp-details')) customElements.define('tls-pdp-details',TLSDetails);
})();
